import axios from "axios";
import { baseURL } from "../Config/config";

// Create Axios Instance
const axiosInstance = axios.create({
  baseURL: baseURL,
  timeout: 10000,
  headers: {
    "X-DTS-SCHEMA": "https://project-lock.universal-service.in",
    "Content-Type": "application/json",
  },
});

// Refresh Handling Controls
let isRefreshing = false;
let refreshSubscribers = [];

// Request Interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    const token = getTokenFromLocalStorage();

    // If no token OR invalid token format → do not attach Authorization
    if (!token) {
      removeTokens();
      return config;
    }

    config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

// Response Interceptor
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized
    if (error.response?.status === 401) {
      removeTokens();

      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const newToken = await refreshAccessToken();

          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          resolveSubscribers(newToken);

          return axiosInstance(originalRequest);
        } catch (err) {
          handleRefreshFailure();
          return Promise.reject(err);
        } finally {
          isRefreshing = false;
        }
      }

      return waitForRefresh(originalRequest);
    }

    return Promise.reject(error);
  }
);

// -----------------------------
// Helper Functions
// -----------------------------

function getTokenFromLocalStorage() {
  const token = localStorage.getItem("token");
  if (isValidJWT(token)) return token;

  localStorage.removeItem("token");
  return null;
}

function getRefreshToken() {
  const refresh = localStorage.getItem("refreshToken");
  if (isValidJWT(refresh)) return refresh;

  localStorage.removeItem("refreshToken");
  return null;
}

function removeTokens() {
  localStorage.removeItem("token");
  localStorage.removeItem("refreshToken");
}

function isValidJWT(token) {
  if (!token || typeof token !== "string") return false;
  return token.split(".").length === 3;
}

async function refreshAccessToken() {
  const refresh = getRefreshToken();
  if (!refresh) throw new Error("No refresh token");

  try {
    const res = await axios.post(`${baseURL}/account/api/token/refresh/`, {
      refresh: refresh,
    });

    const newToken = res.data.access;

    if (!isValidJWT(newToken)) throw new Error("Invalid token returned");

    localStorage.setItem("token", newToken);
    return newToken;
  } catch (err) {
    throw new Error("Failed to refresh token");
  }
}

function resolveSubscribers(newToken) {
  refreshSubscribers.forEach((callback) => callback(newToken));
  refreshSubscribers = [];
}

function waitForRefresh(originalRequest) {
  return new Promise((resolve) => {
    refreshSubscribers.push((token) => {
      originalRequest.headers.Authorization = `Bearer ${token}`;
      resolve(axiosInstance(originalRequest));
    });
  });
}

function handleRefreshFailure() {
  removeTokens();
  window.location.href = "/login";
}

export default axiosInstance;